import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { mayPromo2025YearProductId } from 'reactApp/appHelpers/featuresHelpers/features/mayPromo2025';
import {
    enabledUpsaleSmallModal,
    enabledUpsaleSmallModalB,
    enabledUpsaleSmallModalC,
} from 'reactApp/appHelpers/featuresHelpers/features/upsaleSmallTariffs';
import { upsaleWithTrial } from 'reactApp/appHelpers/featuresHelpers/features/upsaleWithTrial';
import { openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { ReferralProgramSelectors } from 'reactApp/modules/referralProgram/referralProgram.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { MobileBuyClickType } from 'reactApp/sections/MobileSubscriptionsPage/TariffCard/TariffCard.types';
import type { TBuyClick } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/TariffPlansCard.types';
import { sendClickAnalytics } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/useBuyClick';
import type { Product } from 'reactApp/types/Billing';
import { sendIosFavorableTariffsAnalytics, sendProfitableTariffsFirstAnalytics } from 'reactApp/utils/analytics';
import { isMonthPeriod } from 'reactApp/utils/Period';
import { sendTmrGoal, TMR3473039 } from 'reactApp/utils/tmr';

export interface MobilePayProps {
    mayPromo2025Enabled?: boolean;
    isTariffsPage?: boolean;
    onBuyClick: TBuyClick;
}

const smallTariff64 = 64;
const smallTariff128 = 128;

export const useMobileBuyClick = ({ mayPromo2025Enabled = false, isTariffsPage, onBuyClick }: MobilePayProps): MobileBuyClickType => {
    const isPaid = useSelector(UserSelectors.isPaidUser);
    const isAnonymous = useSelector(UserSelectors.isAnonymous);
    const isAlmostFullFreeUser = useSelector(UserSelectors.isAlmostFullFreeUser);

    const isReferralLanding = useSelector(ReferralProgramSelectors.getRefId);

    return useCallback(
        ({ selectedProduct, yearProduct, yearSavingDiscount, paySource }) => {
            const sendAnalytics = () => {
                sendIosFavorableTariffsAnalytics(['buy_click']);
                sendProfitableTariffsFirstAnalytics(['buy_click']);
            };

            if (mayPromo2025Enabled && selectedProduct.id === mayPromo2025YearProductId) {
                sendTmrGoal({ id: TMR3473039, goal: 'click-trial-tariff' });
            }

            if (
                enabledUpsaleSmallModal &&
                enabledUpsaleSmallModalB &&
                (selectedProduct.space.space === smallTariff64 || selectedProduct.space.space === smallTariff128)
            ) {
                openPopupHelper({
                    popupName: popupNames.UPSALE_SMALL_MODAL,
                    data: {
                        isPhone: true,
                        selectedProduct,
                        activePeriod: selectedProduct.period === '1m' ? 'm' : 'y',
                        resolve: (product: Product) => {
                            onBuyClick({
                                product,
                                paySource: 'upsale_unlimited_autoload',
                                space: selectedProduct.space.space,
                                skipClickAnalytics: true,
                            });
                        },
                    },
                });

                return;
            }

            if (
                enabledUpsaleSmallModal &&
                enabledUpsaleSmallModalC &&
                (selectedProduct.space.space === smallTariff64 || selectedProduct.space.space === smallTariff128)
            ) {
                openPopupHelper({
                    popupName: popupNames.UPSALE_SMALL_DIALOG,
                    data: {
                        isPhone: true,
                        selectedProduct,
                        activePeriod: selectedProduct.period === '1m' ? 'm' : 'y',
                        monthProduct: selectedProduct,
                        yearProduct,
                        yearSavingDiscount,
                        resolve: (product: Product) => {
                            onBuyClick({
                                product,
                                paySource: 'upsale_unlimited_autoload',
                                space: selectedProduct.space.space,
                                skipClickAnalytics: true,
                            });
                        },
                    },
                });

                return;
            }

            if (
                !isPaid &&
                // tempexp_18086-next-line
                !isReferralLanding &&
                isTariffsPage &&
                upsaleWithTrial?.tariffs?.length &&
                isMonthPeriod(selectedProduct.period) &&
                yearProduct
            ) {
                sendClickAnalytics({ product: selectedProduct, paySource: 'modal_choose_tariff', isAlmostFullFreeUser });
                openPopupHelper({
                    popupName: popupNames.UPSALE_WITH_TRIAL,
                    data: {
                        isPhone: true,
                        monthProduct: selectedProduct,
                        yearProduct,
                        yearSavingDiscount,
                        activePeriod: upsaleWithTrial.activePeriod,
                        resolve: (product: Product) => {
                            onBuyClick({
                                product,
                                paySource: 'modal_choose_tariff',
                                onClick: sendAnalytics,
                                space: selectedProduct.space.space,
                                skipClickAnalytics: true,
                            });
                        },
                    },
                });
                sendTmrGoal({ id: TMR3473039, goal: 'tariff-click' });

                if (isAnonymous) {
                    sendTmrGoal({ id: TMR3473039, goal: 'login-view' });
                }

                return;
            }

            onBuyClick({
                product: { ...selectedProduct, price: selectedProduct.price || 0 },
                onClick: sendAnalytics,
                // tempexp_16481-next-line
                space: selectedProduct.space.space,
                paySource,
            });
        },
        [onBuyClick, isTariffsPage, isPaid]
    );
};
